package com.fireavert.react.pages.properties

import com.fireavert.common.TableColumn
import com.fireavert.gateways.gateway_property_page.client.presentation.GatewayPropertyController
import com.fireavert.gateways.gateway_property_page.client.presentation.GatewayPropertyViewModel
import com.fireavert.gateways.shared.data_models.GatewayModel
import com.fireavert.react.auth.PermissionRequired
import com.fireavert.react.pages.properties.breadcrumbs.property_details.dialogs.GatewayDialog
import com.fireavert.react.pages.properties.breadcrumbs.property_details.styles.HeadingStyles
import com.fireavert.react.shared.components.forms.SearchableCheckboxList
import com.fireavert.react.shared.components.forms.SearchableCheckboxListProps
import com.fireavert.react.shared.components.tables.SortBar
import com.fireavert.react.shared.components.tables.mapToControlColumns
import com.fireavert.react.shared.components.tables.mapToStateColumns
import com.fireavert.react.shared.styles.HelpStyles
import com.fireavert.react.shared.styles.TableStyles
import com.fireavert.units.shared.data_models.UnitStatus
import com.fireavert.user.data_models.Permission
import com.fireavert.utilities.getKoinInstance
import emotion.css.ClassName
import emotion.css.cx
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.launch
import mui.material.*
import mui.system.sx
import react.*
import react.dom.html.ReactHTML.a
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.img
import react.dom.onChange
import web.cssom.*
import web.dom.ElementId
import web.html.HTMLInputElement
import web.window.WindowTarget
import web.window.window


external interface GatewayDetailsProps : Props {
    var propsIsAdmin: Boolean
    var propsIsMobile: Boolean
    var propertyId: Int
    var propsPropertyName: String
}

val GatewayDetails = FC<GatewayDetailsProps> { props ->
    val controller = getKoinInstance<GatewayPropertyController>()
    val viewModel = getKoinInstance<GatewayPropertyViewModel>()

    var loading: Boolean by useState(true)
    var disableRefreshGatewayStatus: Boolean by useState(false)
    var gatewayDeviceDataLoading by useState(false)
    var showEditView: Boolean by useState(false)

    var associatedGateways: List<GatewayModel> by useState(emptyList())
    var allGateways: List<GatewayModel> by useState(emptyList())
    var selectedGateways by useState(associatedGateways.toSet())
    var deletedGateways by useState(emptySet<GatewayModel>())
    val (selectedGateway, setSelectedGateway) = useState<GatewayModel?>(null)
    var gatewayDeviceData by useState(emptyMap<String, List<String>>())

    var imageUploadError by useState("")

    val (searchValue, setSearchValue) = useState("")
    val (openDialog, setOpenDialog) = useState(false)

    var stateColumns: Array<TableColumn> by useState(
        arrayOf(
            TableColumn(
                text = "NAME",
                sortOrder = TableColumn.SortOrder.Neutral,
                alignment = TableColumn.Alignment.Left
            ),
            TableColumn(
                text = "DESCRIPTION",
                sortOrder = TableColumn.SortOrder.Neutral,
                alignment = TableColumn.Alignment.Left
            ),
            TableColumn(
                text = "GEO LOCATION",
                sortOrder = TableColumn.SortOrder.Neutral,
                alignment = TableColumn.Alignment.Left
            ),
            TableColumn(
                text = "STATUS",
                sortOrder = TableColumn.SortOrder.Neutral,
                alignment = TableColumn.Alignment.Left
            )
        )
    )

    useEffectOnceWithCleanup {
        val viewStateJob = viewModel.viewState.onEach { viewState ->
            associatedGateways = viewState.associatedGateways
            loading = viewState.loading
            allGateways = viewState.allGateways
            imageUploadError = viewState.imageUploadError
            gatewayDeviceData = viewState.gatewayDeviceData
            gatewayDeviceDataLoading = viewState.gatewayDeviceDataLoading

        }.launchIn(viewModel.scope)

        val onLoadJob = viewModel.scope.launch {
            // Only load data if propertyId > 0 (parent has loaded data)
            if (props.propertyId > 0) {
                controller.onLoad(props.propertyId)
            }
        }

        onCleanup {
            onLoadJob.cancel()
            viewStateJob.cancel()
        }
    }
    
    // Watch for changes to propertyId and reload data when it changes from 0 to a valid ID
    useEffect(props.propertyId) {
        if (props.propertyId > 0) {
            viewModel.scope.launch {
                controller.onLoad(props.propertyId)
            }
        }
    }
    
    Accordion {
        AccordionSummary {
            id = ElementId("gateway-panel")
            expandIcon = ReactNode(
                arrayOf(
                    img.create {
                        src = "/static/accordion_arrow_black.svg"
                        alt = "Expand"
                    }
                )
            )
            div {
                className = HeadingStyles.titleHeadingWhiteBackground
                +"Gateways"
            }
        }
        AccordionDetails {
            div {
                className = TableStyles.tableContainerAccordion
                div {
                    className = TableStyles.tableTitle
                    +"Associated Gateways"
                    div {
                        className = TableStyles.duelButtonContainer
                        Button {
                            color = ButtonColor.secondary
                            variant = ButtonVariant.contained
                            disabled = disableRefreshGatewayStatus
                            onClick = {
                                viewModel.scope.launch {
                                    disableRefreshGatewayStatus = true
                                    controller.refreshGatewayStatus(props.propertyId)
                                }
                            }
                            +"Refresh Gateway Status"
                        }

                        PermissionRequired {
                            permission = Permission.EditGateways
                            if (!gatewayDeviceDataLoading) {
                                Button {
                                    color = ButtonColor.secondary
                                    variant = ButtonVariant.contained
                                    onClick = {
                                        viewModel.scope.launch {
                                            controller.loadGatewayDeviceData(props.propertyId.toString())
                                        }
                                    }
                                    +"Device Connection Info"
                                }
                            } else {
                                CircularProgress {
                                    color = CircularProgressColor.info
                                }
                            }
                            Button {
                                color = ButtonColor.primary
                                variant = ButtonVariant.contained
                                onClick = {
                                    if (showEditView) {
                                        showEditView = false
                                        if (selectedGateways.isNotEmpty() || deletedGateways.isNotEmpty()) {
                                            viewModel.scope.launch {
                                                controller.saveAssociatedGateways(
                                                    deletedGateways.toList(),
                                                    selectedGateways.toList(),
                                                    associatedGateways,
                                                    props.propertyId
                                                )
                                                window.location.reload()
                                            }
                                            selectedGateways = emptySet()
                                        }
                                    } else
                                        showEditView = true
                                }
                                +if (showEditView) "Save" else "Add/Edit Gateways"
                            }
                        }
                    }
                }
                div {
                    className = TableStyles.table
                    SortBar {
                        columns = mapToControlColumns(stateColumns)
                        includeLoading = false
                        isLoading = false
                        columnsChanged = {
                            stateColumns = mapToStateColumns(it)
                        }
                    }
                    (associatedGateways + selectedGateways).forEach { gateway ->
                        val isDeleted = deletedGateways.contains(gateway)
                        div {
                            onClick = {
                                setSelectedGateway(gateway)
                                setOpenDialog(true)
                            }
                            className = cx(TableStyles.tableRow, ClassName {
                                textDecoration = if (isDeleted) TextDecoration.lineThrough else None.none
                                color = if (isDeleted) Color("#999999") else Color("inherit")
                                cursor = Cursor.pointer})
                            div {
                                img {
                                    src = "/static/gateway_icon.svg"
                                    alt = "Gateway"
                                }
                                className = TableStyles.tableCell
                                +" ${gateway.name}"
                            }
                            div {
                                className = TableStyles.tableCell
                                +gateway.description.ifEmpty { "N/A" }
                            }
                            div {
                                className = TableStyles.tableCell
                                if (gateway.latitude == 0.0 || gateway.longitude == 0.0) {
                                    +"N/A"
                                } else {
                                    a {
                                        href = generateGoogleEarthLink(gateway.latitude, gateway.longitude)
                                        target = WindowTarget._blank
                                        rel = "noreferrer"
                                        +"View on Google Earth"
                                    }
                                }
                            }
                            div {
                                className = when (gateway.status) {
                                    UnitStatus.Online -> cx(TableStyles.tableCell, TableStyles.greenBold)
                                    UnitStatus.Offline -> cx(TableStyles.tableCell, TableStyles.yellowBold)
                                    else -> cx(TableStyles.tableCell, TableStyles.greyBold)
                                }
                                +when (gateway.status) {
                                    UnitStatus.Online -> "Online"
                                    UnitStatus.Offline -> "Offline"
                                    UnitStatus.Error -> "Never Installed"
                                    UnitStatus.NeverInstalled -> "Never Installed"
                                }
                            }
                        }
                    }

                    if (openDialog && selectedGateway != null) {
                        GatewayDialog {
                            propsPropertyName = props.propsPropertyName
                            propsGatewayName = selectedGateway.name
                            propsGatewayDescription = selectedGateway.description
                            propsGatewayLastUplink = selectedGateway.lastUplink
                            propsGatewaysStatus = selectedGateway.status
                            propsGatewayId = selectedGateway.gatewayId
                            propsController = controller
                            propsImageUploadError = imageUploadError
                            propsPropertyId = props.propertyId
                            propsGatewayLatitude = selectedGateway.latitude ?: 0.0
                            propsGatewayLongitude = selectedGateway.longitude ?: 0.0
                            onClose = { setOpenDialog(false) }
                        }
                    }
                }
            }
            if(showEditView) {
                div {
                    className = HeadingStyles.titleHeadingWhiteBackground
                    +"Add/Remove Gateways"
                }
                TextField {
                    value = searchValue
                    onChange = { event ->
                        val target = event.target as? HTMLInputElement
                        setSearchValue(target?.value ?: "")
                    }
                    placeholder = "Search gateways..."
                    fullWidth = true
                    sx {
                        marginBottom = 2.px
                    }
                }

                SearchableCheckboxList<SearchableCheckboxListProps<GatewayModel>> {
                    items = allGateways
                    selectedItems = selectedGateways.toList()
                    associatedItems = associatedGateways
                    deletedItems = deletedGateways.toList()
                    this.searchValue = searchValue
                    getSearchString = { gateway ->
                        "${gateway.name} ${gateway.description}"
                    }
                    getPrimaryText = { gateway -> gateway.name }
                    getSecondaryText = { gateway -> gateway.description }
                    onSelectionChange = { newSelected, newDeleted ->
                        selectedGateways = newSelected.toSet()
                        deletedGateways = newDeleted.toSet()
                    }
                    paperHeight = 400
                }
            }

            div {
                for ((key, value) in gatewayDeviceData) {
                    div {
                        className = HelpStyles.eventTitle
                        +"$key - # of devices connected: ${value.size}"
                    }
                    div {
                        +"Sensors: ${value.joinToString(", ")}"
                    }
                }
            }
            div {
                className = TableStyles.tableRow

            }
        }
    }
}
fun generateGoogleEarthLink(latitude: Double?, longitude: Double?): String {
    if (latitude == null || longitude == null) {
        return "https://earth.google.com/web/"
    }

    return "https://earth.google.com/web/@$latitude,$longitude,0a,1000d,35y,0h,0t,0r"
}